import * as THREE from 'three';
import type { DistrictKind } from '@somnhold/content';
import { districtCenter, hexLen, isInside, isWall, withDistrict, type MapDef } from '@somnhold/sim';
import { Kit, place } from '../seasons/kit.ts';
import { WORLD_PER_HEX } from './coords.ts';
import { DISTRICT_LOOK } from './districts.ts';
import type { Vfx } from './vfx.ts';
import { cellWorld, wallGraph } from './wallgraph.ts';

/**
 * The Great Work on the board (docs/ERAS.md): scaffolding that climbs the keep while the next district
 * is being raised, the ghost of that district on its side of the bastion (also shown while a player
 * picks a side), and the moment a finished district rises (the light pillar; the walls themselves rise
 * in world.ts). Everything here is decoration: a handful of draw calls, no lights.
 */

export interface GreatWorkView {
  side: number;
  kind: DistrictKind;
  stage: 'blueprint' | 'building';
  /** 0..1 */
  progress: number;
}

export interface DistrictPreview {
  side: number;
  kind: DistrictKind;
  /** False: the side can't take it (drawn red). */
  ok: boolean;
}

const BLUEPRINT = new THREE.Color('#7fb8ff');
const INVALID = new THREE.Color('#ff4f3a');
const WOOD = '#5b4838';
const WOOD_D = '#3e3128';
const ROPE = '#8a7a62';

const V = (x: number, y: number, z: number): THREE.Vector3 => new THREE.Vector3(x, y, z);
const hash = (n: number): number => {
  let h = Math.imul(n ^ 0x2c1b3c6d, 0x297a2d39);
  h = (h ^ (h >>> 15)) >>> 0;
  return h / 4294967296;
};

const P = (() => ({
  box: new THREE.BoxGeometry(1, 1, 1),
  cyl: new THREE.CylinderGeometry(0.5, 0.5, 1, 6),
  wheel: new THREE.CylinderGeometry(0.5, 0.5, 1, 12, 1, true),
  rock: new THREE.DodecahedronGeometry(0.5, 0),
}))();

/* ---------- ghost district: glowing see-through wall and ground ---------- */

const GHOST_VERT = /* glsl */ `
varying vec3 vW;
void main() {
  vec4 w = modelMatrix * vec4(position, 1.0);
  vW = w.xyz;
  gl_Position = projectionMatrix * viewMatrix * w;
}`;

/** The ghost wall grows from its foot (`aY0`) to `uGrow` of its height. */
const WALL_VERT = /* glsl */ `
attribute float aY0;
uniform float uGrow;
uniform float uH;
varying float vY;
void main() {
  vec3 p = position;
  p.y = aY0 + (p.y - aY0) * uGrow;
  vY = (p.y - aY0) / (uH * uGrow);
  gl_Position = projectionMatrix * viewMatrix * modelMatrix * vec4(p, 1.0);
}`;

/** The wall: brightest along its top and foot, a band of light climbing it, a slow breath. */
const WALL_FRAG = /* glsl */ `
uniform vec3 uColor;
uniform float uTime;
uniform float uA;
varying float vY;
void main() {
  float y = clamp(vY, 0.0, 1.0);
  float edge = smoothstep(0.86, 1.0, y) + (1.0 - smoothstep(0.0, 0.12, y)) * 0.6;
  float band = exp(-pow((y - fract(uTime * 0.35)) * 9.0, 2.0)) * 0.55;
  float breath = 0.8 + 0.2 * sin(uTime * 2.2);
  float a = (0.16 + edge * 0.7 + band) * breath * uA;
  gl_FragColor = vec4(uColor * (1.0 + edge * 0.6), a);
  #include <colorspace_fragment>
}`;

/** The ground: each hex tinted, a ripple running out from the district's centre. */
const GROUND_FRAG = /* glsl */ `
uniform vec3 uColor;
uniform float uTime;
uniform vec2 uC;
uniform float uA;
varying vec3 vW;
void main() {
  float d = length(vW.xz - uC);
  float ripple = 0.5 + 0.5 * sin(d * 2.4 - uTime * 2.6);
  float a = (0.14 + 0.16 * ripple) * uA;
  gl_FragColor = vec4(uColor, a);
  #include <colorspace_fragment>
}`;

const ghostMat = (frag: string, uniforms: Record<string, THREE.IUniform>, vert = GHOST_VERT): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    vertexShader: vert,
    fragmentShader: frag,
    uniforms,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    side: THREE.DoubleSide,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -4,
  });

/** The light pillar of a district rising. */
const PILLAR_VERT = /* glsl */ `
varying vec2 vUv;
varying float vFace;
void main() {
  vUv = uv;
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  vec3 n = normalize(normalMatrix * normal);
  vFace = abs(dot(n, normalize(-mv.xyz)));
  gl_Position = projectionMatrix * mv;
}`;

const PILLAR_FRAG = /* glsl */ `
uniform vec3 uColor;
uniform float uA;
varying vec2 vUv;
varying float vFace;
void main() {
  // Soft at its silhouette and its foot, fading up into the sky.
  float a = pow(1.0 - vUv.y, 1.6) * smoothstep(0.0, 0.06, vUv.y) * (0.25 + 0.75 * vFace) * uA;
  gl_FragColor = vec4(uColor, a);
  #include <colorspace_fragment>
}`;

class Ghost {
  readonly group = new THREE.Group();
  private wall: THREE.Mesh | null = null;
  private ground: THREE.Mesh | null = null;
  private wallU = { uColor: { value: BLUEPRINT.clone() }, uTime: { value: 0 }, uH: { value: 1.1 }, uA: { value: 1 }, uGrow: { value: 1 } };
  private groundU = { uColor: { value: BLUEPRINT.clone() }, uTime: { value: 0 }, uC: { value: new THREE.Vector2() }, uA: { value: 1 } };
  private wallMat = ghostMat(WALL_FRAG, this.wallU, WALL_VERT);
  private groundMat = ghostMat(GROUND_FRAG, this.groundU);
  private key = '';

  constructor(
    private map: MapDef,
    private isWallNow: (q: number, r: number) => boolean,
    private land: (x: number, z: number) => number,
  ) {
    this.group.name = 'district-ghost';
    this.group.renderOrder = 6;
  }

  /** Shows the district-to-be on `side` (null hides it). `height` 0..1 of the full ghost wall. */
  show(d: { side: number; kind: DistrictKind } | null, color: THREE.Color, height = 1, alpha = 1): void {
    this.group.visible = !!d;
    if (!d) return;
    const key = `${d.side}:${d.kind}`;
    if (key !== this.key) this.build(d.side, d.kind);
    this.key = key;
    this.wallU.uColor.value.copy(color);
    this.groundU.uColor.value.copy(color);
    this.wallU.uA.value = alpha;
    this.groundU.uA.value = alpha;
    this.wallU.uGrow.value = 0.35 + 0.65 * height;
  }

  private build(side: number, kind: DistrictKind): void {
    for (const m of [this.wall, this.ground]) if (m) {
      this.group.remove(m);
      m.geometry.dispose();
    }
    const map = this.map;
    // The map with that district: the real one when the side can take it, else the bare shape.
    const next = withDistrict(map, { side, kind }) ?? { ...map, districts: [...(map.districts ?? []), { side, kind }] };
    const gates = new Set(next.sectors.map((s) => `${s.gate.q},${s.gate.r}`));
    const g = wallGraph(next, (q, r) => isWall(next, q, r), (q, r) => gates.has(`${q},${r}`));
    const fresh = (q: number, r: number): boolean => !this.isWallNow(q, r);
    // Where the ghost stands: on the land as it lies now (outside the walls it rolls).
    const rad = (WORLD_PER_HEX / Math.sqrt(3)) * 0.9;
    const floor = (x: number, z: number): number => {
      let h = Math.max(0, this.land(x, z));
      for (let i = 0; i < 6; i++) {
        const a = (Math.PI / 3) * i;
        h = Math.max(h, this.land(x + Math.cos(a) * rad, z + Math.sin(a) * rad));
      }
      return h;
    };
    const wall = new Kit({ aY0: 0 });
    const H = 0.8;
    for (const l of g.links) {
      if (!fresh(l.a.q, l.a.r) && !fresh(l.b.q, l.b.r)) continue;
      const mid = l.a.p.clone().add(l.b.p).multiplyScalar(0.5);
      const len = l.a.p.distanceTo(l.b.p);
      const yaw = Math.atan2(-(l.b.p.z - l.a.p.z), l.b.p.x - l.a.p.x);
      const y0 = Math.max(floor(l.a.p.x, l.a.p.z), floor(l.b.p.x, l.b.p.z)) * 0.7;
      wall.add(P.box, '#ffffff', place(mid.x, y0 + H / 2 - 0.15, mid.z, yaw, V(len, H + 0.3, 0.3)), undefined, { aY0: y0 - 0.3 });
    }
    for (const c of g.cells.values()) {
      if (!fresh(c.q, c.r) || c.gate) continue;
      const y0 = floor(c.p.x, c.p.z) * 0.7;
      wall.add(P.cyl, '#ffffff', place(c.p.x, y0 + (H + 0.2) / 2 - 0.15, c.p.z, 0, V(0.5, H + 0.5, 0.5)), undefined, { aY0: y0 - 0.3 });
    }
    this.wall = wall.build(this.wallMat, 'ghost-wall');
    // The ground the district takes in (the old wall's stretch included).
    const ground = new Kit();
    const hex = new THREE.CircleGeometry((WORLD_PER_HEX / Math.sqrt(3)) * 0.9, 6).rotateX(-Math.PI / 2).rotateY(Math.PI / 6);
    const R = map.radius;
    for (let r = -R; r <= R; r++) for (let q = -R; q <= R; q++) {
      if (hexLen(q, r) > R || !isInside(next, q, r) || isInside(map, q, r)) continue;
      const p = cellWorld(q, r);
      ground.add(hex, '#ffffff', place(p.x, floor(p.x, p.z) + 0.07, p.z));
    }
    hex.dispose();
    this.ground = ground.build(this.groundMat, 'ghost-ground');
    const c = districtCenter(map.bastion, side);
    const cw = cellWorld(c.q, c.r);
    this.groundU.uC.value.set(cw.x, cw.z);
    for (const m of [this.ground, this.wall]) if (m) {
      m.frustumCulled = false;
      m.renderOrder = 6;
      this.group.add(m);
    }
  }

  frame(t: number): void {
    this.wallU.uTime.value = t;
    this.groundU.uTime.value = t;
  }

  dispose(): void {
    for (const m of [this.wall, this.ground]) m?.geometry.dispose();
    this.wallMat.dispose();
    this.groundMat.dispose();
  }
}

/* ---------- the scaffold on the keep ---------- */

/** Scaffold pieces appear in order of `aReveal` as the work goes on (one draw, no rebuilds). */
const revealMat = (u: { uProg: { value: number } }): THREE.MeshStandardMaterial => {
  const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.9, metalness: 0, flatShading: true });
  mat.onBeforeCompile = (sh) => {
    Object.assign(sh.uniforms, u);
    sh.vertexShader = 'attribute float aReveal;\nuniform float uProg;\n' + sh.vertexShader.replace(
      '#include <begin_vertex>',
      '#include <begin_vertex>\nif (aReveal > uProg) transformed = vec3(0.0, -60.0, 0.0);',
    );
  };
  mat.customProgramCacheKey = () => 'somnhold-reveal';
  return mat;
};

interface Crane {
  group: THREE.Group;
  jib: THREE.Group;
  block: THREE.Object3D;
  at: number;
  ph: number;
}

class Scaffold {
  readonly group = new THREE.Group();
  private u = { uProg: { value: 0 } };
  private mat = revealMat(this.u);
  private mesh: THREE.Mesh | null;
  private cranes: Crane[] = [];
  /** Where golems are working (keep-local), with the share of the work at which each appears. */
  readonly spots: { p: THREE.Vector3; at: number }[] = [];
  progress = 0;

  constructor(keep: THREE.Matrix4) {
    this.group.name = 'great-work';
    const kit = new Kit({ aReveal: 0 });
    const S = 0.66;
    const levels = [0.42, 0.84, 1.26, 1.68, 2.1];
    const rv = (a: number): Record<string, number> => ({ aReveal: a });
    // Posts around the keep, a ring of ledgers and decks per level, braces and ladders; it climbs as
    // the work goes on (level i appears around i / levels of the way).
    const posts: [number, number][] = [];
    for (const s of [-1, 1]) for (const t of [-S, -S / 3, S / 3, S]) {
      posts.push([t, s * S]);
      if (Math.abs(t) < S) posts.push([s * S, t]);
    }
    levels.forEach((y, li) => {
      const a0 = li / levels.length;
      const span = 1 / levels.length;
      posts.forEach(([x, z], i) => {
        const a = a0 + (i / posts.length) * span * 0.5;
        kit.add(P.cyl, WOOD, keep, place(x, y - 0.21, z, 0, V(0.035, 0.44, 0.035), (hash(i + li * 13) - 0.5) * 0.04), rv(a));
      });
      for (const s of [-1, 1]) {
        const a = a0 + span * (s > 0 ? 0.55 : 0.65);
        kit.add(P.box, WOOD_D, keep, place(0, y, s * S, 0, V(2 * S + 0.08, 0.03, 0.035)), rv(a));
        kit.add(P.box, WOOD_D, keep, place(s * S, y, 0, 0, V(0.035, 0.03, 2 * S + 0.08)), rv(a + span * 0.05));
        // Decks of planks on two sides of each level, swapping sides as it climbs.
        const deckZ = li % 2 === 0;
        const plank = (k: number): THREE.Matrix4 =>
          deckZ ? place(-S + 0.1 + k * 0.2, y + 0.022, s * (S - 0.12), (hash(k + li) - 0.5) * 0.05, V(0.19, 0.018, 0.24)) : place(s * (S - 0.12), y + 0.022, -S + 0.1 + k * 0.2, (hash(k + li) - 0.5) * 0.05, V(0.24, 0.018, 0.19));
        for (let k = 0; k < 7; k++) if (hash(k * 7 + li * 3 + s) > 0.12) kit.add(P.box, WOOD, keep, plank(k), rv(a + span * 0.1 + k * 0.01));
        // A diagonal brace across each face.
        const ang = Math.atan2(0.42, 2 * S) * (li % 2 ? 1 : -1);
        kit.add(P.box, WOOD_D, keep, place(0, y - 0.21, s * (S + 0.03), 0, V(2 * S * 1.04, 0.025, 0.025), 0, ang), rv(a + span * 0.3));
        kit.add(P.box, WOOD_D, keep, place(s * (S + 0.03), y - 0.21, 0, Math.PI / 2, V(2 * S * 1.04, 0.025, 0.025), 0, -ang), rv(a + span * 0.35));
      }
      // A ladder up to this level, on alternating corners.
      const lx = li % 2 ? S - 0.18 : -S + 0.18;
      const lz = S + 0.07;
      for (const k of [-1, 1]) kit.add(P.box, WOOD, keep, place(lx + k * 0.06, y - 0.21, lz, 0, V(0.02, 0.46, 0.02), -0.12), rv(a0 + span * 0.2));
      for (let k = 0; k < 4; k++) kit.add(P.box, WOOD, keep, place(lx, y - 0.38 + k * 0.1, lz + 0.03 - k * 0.012, 0, V(0.12, 0.015, 0.015)), rv(a0 + span * 0.2));
      // Ropes hanging from the deck edge, with a bucket now and then.
      for (let k = 0; k < 2; k++) {
        const x = (hash(li * 5 + k) - 0.5) * S * 1.6;
        const len = 0.3 + hash(li * 9 + k) * 0.25;
        const zs = k ? -1 : 1;
        kit.add(P.cyl, ROPE, keep, place(x, y - len / 2, zs * (S + 0.05), 0, V(0.008, len, 0.008)), rv(a0 + span * 0.7));
        if (hash(li + k * 3) > 0.4) kit.add(P.cyl, WOOD_D, keep, place(x, y - len - 0.03, zs * (S + 0.05), 0, V(0.06, 0.06, 0.06)), rv(a0 + span * 0.7));
      }
      this.spots.push({ p: V(0, y + 0.05, S * (li % 2 ? -1 : 1)), at: a0 + span * 0.2 });
    });
    // The stone collar: courses of new masonry closing round the keep's foot.
    const courses = 4;
    for (let c = 0; c < courses; c++) {
      const n = 22;
      for (let i = 0; i < n; i++) {
        const ang = (i / n) * Math.PI * 2 + (c % 2 ? Math.PI / n : 0);
        const r = 0.86 - c * 0.025;
        const grey = new THREE.Color().setHSL(0.6, 0.03, 0.42 + hash(i * 3 + c * 41) * 0.12);
        const a = 0.04 + (c / courses) * 0.8 + (i / n) * (0.8 / courses);
        kit.add(P.box, grey, keep, place(Math.cos(ang) * r, 0.05 + c * 0.1, Math.sin(ang) * r, -ang + Math.PI / 2, V(0.235, 0.095, 0.16)), rv(a));
      }
    }
    // Stone and timber waiting at the foot (there from the first stroke).
    for (let i = 0; i < 7; i++) {
      const row = i < 4 ? 0 : i < 6 ? 1 : 2;
      const x = row === 0 ? -0.24 + i * 0.16 : row === 1 ? -0.16 + (i - 4) * 0.16 : -0.08;
      kit.add(P.box, '#9a9b9e', keep, place(1.08 + (hash(i) - 0.5) * 0.04, 0.06 + row * 0.11, x, (hash(i + 9) - 0.5) * 0.2, V(0.14, 0.1, 0.15)), rv(0));
    }
    for (let i = 0; i < 5; i++) kit.add(P.cyl, WOOD, keep, place(-1.06, 0.05 + (i > 2 ? 0.08 : 0), -0.16 + (i % 3) * 0.1 + (i > 2 ? 0.05 : 0), 0, V(0.075, 0.6, 0.075), Math.PI / 2), rv(0.01));
    this.mesh = kit.build(this.mat, 'great-work-scaffold');
    if (this.mesh) {
      this.mesh.frustumCulled = false;
      this.group.add(this.mesh);
    }
    // Two treadwheel cranes at opposite corners, swinging blocks up the keep.
    for (const [i, cx, cz, at] of [[0, 1.12, -1.12, 0.12], [1, -1.12, 1.12, 0.45]] as const) {
      const crane = new Kit();
      const H = 2.5;
      crane.add(P.box, WOOD_D, new THREE.Matrix4(), place(0, 0.05, 0, 0, V(0.32, 0.08, 0.32)));
      for (const s of [-1, 1]) crane.add(P.box, WOOD, new THREE.Matrix4(), place(s * 0.1, H / 2, 0, 0, V(0.045, H, 0.045), 0, -s * 0.03));
      for (let k = 1; k < 6; k++) crane.add(P.box, WOOD_D, new THREE.Matrix4(), place(0, (k * H) / 6, 0, 0, V(0.22, 0.02, 0.02)));
      // The treadwheel at its foot.
      crane.add(P.wheel, WOOD, new THREE.Matrix4(), place(0, 0.32, 0.2, 0, V(0.5, 0.12, 0.5), Math.PI / 2));
      for (let k = 0; k < 4; k++) crane.add(P.box, WOOD_D, new THREE.Matrix4(), place(0, 0.32, 0.2, 0, V(0.02, 0.5, 0.02), 0, (k * Math.PI) / 4));
      const g = new THREE.Group();
      const base = crane.build(new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.9, flatShading: true }), 'crane');
      if (base) g.add(base);
      // The jib swings; the block hangs from its end.
      const jib = new THREE.Group();
      jib.position.y = H;
      const jk = new Kit();
      jk.add(P.box, WOOD, new THREE.Matrix4(), place(0, 0, 0.45, 0, V(0.05, 0.05, 1.5), -0.1));
      jk.add(P.box, WOOD_D, new THREE.Matrix4(), place(0, 0.02, -0.38, 0, V(0.16, 0.12, 0.16)));
      jk.add(P.cyl, ROPE, new THREE.Matrix4(), place(0, 0.12, 0.2, 0, V(0.008, 0.02, 0.008)));
      const jm = jk.build(new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.9, flatShading: true }), 'jib');
      if (jm) jib.add(jm);
      const block = new THREE.Group();
      block.position.set(0, -0.08, 1.1);
      const bk = new Kit();
      bk.add(P.cyl, ROPE, new THREE.Matrix4(), place(0, -0.5, 0, 0, V(0.01, 1, 0.01)));
      bk.add(P.box, '#a3a4a7', new THREE.Matrix4(), place(0, -1.06, 0, 0, V(0.18, 0.13, 0.15)));
      const bm = bk.build(new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.9, flatShading: true }), 'block');
      if (bm) block.add(bm);
      jib.add(block);
      g.add(jib);
      const kp = V(cx, 0, cz).applyMatrix4(keep);
      g.position.copy(kp).setY(new THREE.Vector3().setFromMatrixPosition(keep).y);
      g.rotation.y = Math.atan2(-kp.x, -kp.z);
      this.group.add(g);
      this.cranes.push({ group: g, jib, block, at, ph: i * 2.1 });
    }
  }

  /** How much of the scaffold stands: 0 … 1. */
  set(p: number): void {
    this.progress = p;
    this.u.uProg.value = p;
    for (const c of this.cranes) c.group.visible = p >= c.at;
  }

  frame(t: number): void {
    for (const c of this.cranes) {
      // Swing out, hoist, swing back over the keep: slow and heavy.
      const s = Math.sin(t * 0.35 + c.ph);
      c.jib.rotation.y = s * 0.55;
      c.block.scale.y = 0.55 + 0.45 * (0.5 + 0.5 * Math.sin(t * 0.7 + c.ph));
    }
  }

  dispose(): void {
    this.group.traverse((o) => {
      const m = o as THREE.Mesh;
      if (m.isMesh) {
        m.geometry.dispose();
        (m.material as THREE.Material).dispose();
      }
    });
  }
}

/* ---------- the layer ---------- */

export class GreatWorkLayer {
  readonly group = new THREE.Group();
  private ghost: Ghost;
  private scaffold: Scaffold | null = null;
  private work: GreatWorkView | null = null;
  private preview: DistrictPreview | null = null;
  private pillar: { mesh: THREE.Mesh; u: { uColor: { value: THREE.Color }; uA: { value: number } }; t0: number } | null = null;
  private lastPuff = 0;

  constructor(
    private map: MapDef,
    private keep: THREE.Matrix4 | null,
    isWallNow: (q: number, r: number) => boolean,
    private vfx: Vfx,
    private low: boolean,
    ground: (x: number, z: number) => number,
  ) {
    this.group.name = 'great-work-layer';
    this.ghost = new Ghost(map, isWallNow, ground);
    this.ghost.group.visible = false;
    this.group.add(this.ghost.group);
  }

  setWork(gw: GreatWorkView | null): void {
    this.work = gw;
    if (gw && this.keep && !this.scaffold) {
      this.scaffold = new Scaffold(this.keep);
      this.group.add(this.scaffold.group);
    }
    if (this.scaffold) {
      this.scaffold.group.visible = !!gw;
      // Blueprint: the first posts and the stone waiting; building: it climbs with the work.
      if (gw) this.scaffold.set(gw.stage === 'blueprint' ? 0.08 : 0.12 + 0.88 * Math.min(1, Math.max(0, gw.progress)));
    }
    this.syncGhost();
  }

  setPreview(p: DistrictPreview | null): void {
    this.preview = p;
    this.syncGhost();
  }

  /** The player's pick wins over the work under way (the ghost shows one district at a time). */
  private syncGhost(): void {
    const p = this.preview;
    const w = this.work;
    if (p) this.ghost.show(p, p.ok ? BLUEPRINT : INVALID, 1, 1);
    else if (w) this.ghost.show(w, BLUEPRINT, w.stage === 'blueprint' ? 0 : Math.min(1, Math.max(0, w.progress)), w.stage === 'blueprint' ? 0.75 : 1);
    else this.ghost.show(null, BLUEPRINT);
  }

  /** A shaft of light over a district rising, fading out over ~2 s. */
  beam(p: THREE.Vector3, kind: DistrictKind, t: number): void {
    if (!this.pillar) {
      const u = { uColor: { value: new THREE.Color() }, uA: { value: 0 } };
      const mat = new THREE.ShaderMaterial({
        vertexShader: PILLAR_VERT,
        fragmentShader: PILLAR_FRAG,
        uniforms: u,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        side: THREE.DoubleSide,
      });
      const mesh = new THREE.Mesh(new THREE.CylinderGeometry(0.8, 0.55, 14, 32, 1, true).translate(0, 7, 0), mat);
      mesh.frustumCulled = false;
      mesh.renderOrder = 12;
      this.group.add(mesh);
      this.pillar = { mesh, u, t0: t };
    }
    this.pillar.mesh.position.copy(p);
    this.pillar.u.uColor.value.set(DISTRICT_LOOK[kind].glow).lerp(new THREE.Color('#ffffff'), 0.55);
    this.pillar.t0 = t;
    this.pillar.mesh.visible = true;
  }

  frame(t: number): void {
    this.ghost.frame(t);
    const sc = this.scaffold;
    if (sc?.group.visible) {
      sc.frame(t);
      // Golems at work: dust puffing from the scaffold where it stands, now and then a spark of chisel.
      const w = this.work;
      if (w?.stage === 'building' && this.keep && t - this.lastPuff > (this.low ? 0.9 : 0.45)) {
        this.lastPuff = t;
        const live = sc.spots.filter((s) => s.at <= sc.progress);
        const s = live[Math.floor(Math.random() * live.length)];
        if (s) {
          const p = s.p.clone().add(V((Math.random() - 0.5) * 0.9, 0, 0)).applyMatrix4(this.keep);
          this.vfx.burst(p, { n: 2, color: '#b9b3a8', speed: 0.02, up: 0.025, size: 0.22, life: 10, smoke: true, spread: 0.2 });
          if (Math.random() < 0.5) this.vfx.burst(p, { n: 3, color: '#ffd9a0', speed: 0.04, up: 0.03, size: 0.04, life: 5, gravity: 0.008 });
        }
      }
    }
    const pl = this.pillar;
    if (pl?.mesh.visible) {
      const k = (t - pl.t0) / 2.2;
      if (k >= 1) pl.mesh.visible = false;
      else {
        // Flash up fast, then thin and fade.
        pl.u.uA.value = (k < 0.05 ? k / 0.05 : Math.pow(1 - (k - 0.05) / 0.95, 2.2)) * 0.95;
        pl.mesh.scale.set(1 + k * 0.6, 1, 1 + k * 0.6);
      }
    }
  }

  dispose(): void {
    this.ghost.dispose();
    this.scaffold?.dispose();
    if (this.pillar) {
      this.pillar.mesh.geometry.dispose();
      (this.pillar.mesh.material as THREE.Material).dispose();
    }
  }
}
